import { ShippingRateDefinitionType } from '../../../../../pb/settings/v1/shipping_rate_definition_type_pb';
import { RateType, ShippingRateDefinition } from '../../../../../pb/shopify/v1/shipping_rates_pb';
import { parseShopifyGidId } from 'hooks/shopify';
import { CommonMapper } from 'utils/mapper/common.mapper';
import { MappingShippingRate, ShippingRateMapping } from '../models/shipping-rate-mapping';

const DEFINITION_TYPE_BY_RATE_TYPE: Record<RateType, ShippingRateDefinitionType> = {
    [RateType.UNSPECIFIED]: ShippingRateDefinitionType.UNSPECIFIED,
    [RateType.FLAT]: ShippingRateDefinitionType.FLAT,
    [RateType.ORDER_AMOUNT]: ShippingRateDefinitionType.ORDER_AMOUNT,
    [RateType.PACKAGE_WEIGHT]: ShippingRateDefinitionType.PACKAGE_WEIGHT,
};

/**
 * A market's shipping rate definition (from `/api/v1/markets`) in the shape the rate mapping PUT
 * takes. Returns `undefined` when its GID has no numeric id to map it by.
 */
export const toMappingShippingRate = (definition: ShippingRateDefinition): MappingShippingRate | undefined => {
    const id = parseShopifyGidId(definition.id);

    if (id === undefined) return undefined;

    // Everything that carries over as-is is spread; only what differs is taken out first: the GID `id`
    // (mapped to the numeric one), the market `RateType` (mapped to the mapping's own enum), the
    // `int64` transit times (mapped to numbers), and each message's `$typeName` / a rate's own GID,
    // which the mapping has no field for.
    const { $typeName, id: gid, type, rates, ...definitionFields } = definition;

    return {
        ...definitionFields,
        id,
        type: DEFINITION_TYPE_BY_RATE_TYPE[type] ?? ShippingRateDefinitionType.UNSPECIFIED,
        rates: (rates ?? []).map(({ $typeName, id: rateGid, minimumTransit, maximumTransit, ...rateFields }) => ({
            ...rateFields,
            minimumTransit: CommonMapper.mapBigintIntoNumber(minimumTransit),
            maximumTransit: CommonMapper.mapBigintIntoNumber(maximumTransit),
        })),
    };
};

/**
 * Everything the rate mapping PUT needs to know about each rate id, merged from the two places a rate
 * can be known from: the saved mapping (a rate saved earlier, possibly since removed from the market)
 * and the live market rates (a freshly picked rate). Live details win, since they're current — the
 * free-from threshold included, so one the merchant removed in Shopify isn't kept alive by the saved
 * mapping. The saved details only fill in a missing name.
 */
export const mergeShippingRateDetails = (
    mappings: ShippingRateMapping[],
    liveRates: Map<number, MappingShippingRate>
): Map<number, MappingShippingRate> => {
    const byId = new Map<number, MappingShippingRate>();

    mappings.forEach((mapping) =>
        mapping.providerRates.forEach((providerRate) =>
            providerRate.shippingRates.forEach((rate) => byId.set(rate.id, rate))
        )
    );

    liveRates.forEach((live, id) => {
        const saved = byId.get(id);

        byId.set(id, { ...saved, ...live, name: live.name || saved?.name || '' });
    });

    return byId;
};

/** The PUT entry for a rate id; a rate known from neither source is sent with just its id. */
export const shippingRateDetailsOf = (details: Map<number, MappingShippingRate>, id: number): MappingShippingRate =>
    details.get(id) ?? { id, name: '', type: ShippingRateDefinitionType.UNSPECIFIED, currency: '', rates: [] };
